import { CategoryNode, CategoryTree } from '../types/category';
import { parseUserDateInput, toIsoDateString } from './dateUtils';
import { amountsForNoVat } from './amountUtils';
import { applyCreditSignToAmounts, looksLikeCreditAmounts } from './creditNotes';
import { isNoVatCategory } from './noVat';

export const MAX_IMPORT_ROWS = 10000;
export const IMPORT_BATCH_SIZE = 200;
export const IMPORT_DATA_SHEET = 'הוצאות';
export const IMPORT_EXAMPLE_VENDOR = 'ספק לדוגמה';

export const IMPORT_HEADERS = {
  vendorName: 'שם ספק',
  netAmount: 'סכום נטו',
  vatAmount: 'סכום מע״מ',
  totalAmount: 'סכום כולל',
  category1: 'קטגוריה L1',
  category2: 'קטגוריה L2',
  category3: 'קטגוריה L3',
  documentDate: 'תאריך חשבונית',
  projectTag: 'תג פרויקט',
  expenseId: 'מזהה הוצאה',
  notes: 'הערות',
} as const;

export const IMPORT_HEADER_ORDER: Array<keyof typeof IMPORT_HEADERS> = [
  'vendorName',
  'netAmount',
  'vatAmount',
  'totalAmount',
  'category1',
  'category2',
  'category3',
  'documentDate',
  'projectTag',
  'expenseId',
  'notes',
];

export type ImportRawRow = {
  row: number;
  vendorName: string;
  netAmount: string;
  vatAmount: string;
  totalAmount: string;
  category1: string;
  documentDate: string;
  category2: string;
  category3: string;
  projectTag: string;
  expenseId: string;
  notes: string;
};

export type ValidImportRow = {
  row: number;
  vendorName: string;
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
  category1: string;
  category2: string;
  category3: string;
  projectTag: string;
  documentDate: string;
  invoiceNumber: string;
  notes: string;
  documentType?: string;
};

export type SkippedImportRow = {
  row: number;
  reason: string;
  kind?: 'invalid' | 'duplicate';
  existingId?: number | string;
};

const QUOTE_RE = /["'`״׳]/g;

export const normalizeHeader = (value: string): string =>
  String(value || '')
    .replace(QUOTE_RE, '')
    .replace(/[־–]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export const HEADER_LOOKUP: Record<string, keyof typeof IMPORT_HEADERS> = Object.entries(
  IMPORT_HEADERS
).reduce((acc, [key, label]) => {
  acc[normalizeHeader(label)] = key as keyof typeof IMPORT_HEADERS;
  return acc;
}, {} as Record<string, keyof typeof IMPORT_HEADERS>);

HEADER_LOOKUP[normalizeHeader('סכום נטו (לפני מע״מ)')] = 'netAmount';
HEADER_LOOKUP[normalizeHeader('סכום כולל (כולל מע״מ)')] = 'totalAmount';
HEADER_LOOKUP[normalizeHeader('סכום')] = 'totalAmount';
HEADER_LOOKUP[normalizeHeader('תג')] = 'projectTag';
HEADER_LOOKUP[normalizeHeader('project_tag')] = 'projectTag';
HEADER_LOOKUP[normalizeHeader('net_amount')] = 'netAmount';
HEADER_LOOKUP[normalizeHeader('vat_amount')] = 'vatAmount';
HEADER_LOOKUP[normalizeHeader('total_amount')] = 'totalAmount';

export const trimText = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim();

const roundImportMoney = (value: number): number => Math.round(value * 100) / 100;

export const parseImportAmount = (value: unknown, allowZero = false): number | null => {
  if (value === null || value === undefined || value === '') return null;
  let parsed: number | null = null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    parsed = roundImportMoney(value);
  } else {
    const raw = String(value).trim().replace(/\s/g, '').replace(/₪/g, '');
    if (!raw || raw === '-' || raw === ',' || raw === '.') return null;
    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    let normalized = raw;
    if (lastComma >= 0 && lastDot >= 0) {
      normalized = lastComma > lastDot ? raw.replace(/\./g, '').replace(',', '.') : raw.replace(/,/g, '');
    } else if (lastComma >= 0) {
      const decimals = raw.length - lastComma - 1;
      normalized = decimals <= 2 ? raw.replace(',', '.') : raw.replace(/,/g, '');
    }
    const num = Number(normalized);
    parsed = Number.isFinite(num) ? roundImportMoney(num) : null;
  }
  if (parsed == null) return null;
  if (parsed === 0) return allowZero ? 0 : null;
  return parsed;
};

export const resolveImportAmounts = (
  netRaw: unknown,
  vatRaw: unknown,
  totalRaw: unknown
): { netAmount: number; vatAmount: number; totalAmount: number } | { error: string } => {
  let netAmount = parseImportAmount(netRaw);
  let vatAmount = parseImportAmount(vatRaw, true);
  let totalAmount = parseImportAmount(totalRaw);
  const isCredit = looksLikeCreditAmounts(netAmount, vatAmount, totalAmount);
  if (netAmount == null && vatAmount == null && totalAmount == null) {
    return { error: 'חסר סכום תקין' };
  }
  if (totalAmount == null) {
    if (netAmount == null) return { error: 'חסר סכום תקין' };
    vatAmount = vatAmount ?? 0;
    totalAmount = roundImportMoney(netAmount + vatAmount);
  } else if (netAmount == null) {
    vatAmount = vatAmount ?? 0;
    netAmount = roundImportMoney(totalAmount - vatAmount);
    if (!isCredit && netAmount < 0) return { error: 'חסר סכום נטו תקין' };
  } else if (vatAmount == null) {
    vatAmount = roundImportMoney(totalAmount - netAmount);
    if (!isCredit && vatAmount < 0) vatAmount = 0;
  }
  if (isCredit) {
    return applyCreditSignToAmounts(netAmount, vatAmount, totalAmount);
  }
  return { netAmount, vatAmount, totalAmount };
};

export const parseImportDate = (value: unknown): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toIsoDateString(new Date(value.getFullYear(), value.getMonth(), value.getDate()));
  }
  const parsed = parseUserDateInput(String(value ?? ''));
  return parsed ? toIsoDateString(parsed) : '';
};

export const findNamedChild = (
  nodes: CategoryNode[] | undefined,
  name: string
): CategoryNode | undefined => {
  const key = trimText(name).toLowerCase();
  if (!key) return undefined;
  return (nodes || []).find((node) => trimText(node.name).toLowerCase() === key);
};

export type ImportListTables = {
  l1: string[];
  l2Parents: string[];
  l2Names: string[];
  l3Keys: string[];
  l3Names: string[];
};

export const categoryPathKey = (l1: string, l2: string): string => `${l1}|${l2}`;

/** Hidden Excel lists for L1 and dependent L2/L3 dropdowns. */
export const buildImportListTables = (tree: CategoryTree | null | undefined): ImportListTables => {
  const l1: string[] = [];
  const l2Parents: string[] = [];
  const l2Names: string[] = [];
  const l3Keys: string[] = [];
  const l3Names: string[] = [];

  (tree?.categories || []).forEach((level1) => {
    if (!level1.name) return;
    l1.push(level1.name);
    const level2Nodes = level1.children || [];
    if (level2Nodes.length === 0) {
      l2Parents.push(level1.name);
      l2Names.push('');
      l3Keys.push(categoryPathKey(level1.name, ''));
      l3Names.push('');
      return;
    }
    level2Nodes.forEach((level2) => {
      l2Parents.push(level1.name);
      l2Names.push(level2.name || '');
      const level3Nodes = level2.children || [];
      if (level3Nodes.length === 0) {
        l3Keys.push(categoryPathKey(level1.name, level2.name || ''));
        l3Names.push('');
        return;
      }
      level3Nodes.forEach((level3) => {
        l3Keys.push(categoryPathKey(level1.name, level2.name || ''));
        l3Names.push(level3.name || '');
      });
    });
  });

  return { l1, l2Parents, l2Names, l3Keys, l3Names };
};

export const isExampleImportRow = (vendorName: string): boolean =>
  trimText(vendorName) === IMPORT_EXAMPLE_VENDOR;

export const resolveImportCategories = (
  tree: CategoryTree | null | undefined,
  category1: string,
  category2: string,
  category3: string
): { names: { category1: string; category2: string; category3: string }; error?: string } => {
  const l1Name = trimText(category1);
  const l2Name = trimText(category2);
  const l3Name = trimText(category3);
  const roots = tree?.categories || [];

  if (!l1Name) return { names: { category1: '', category2: '', category3: '' }, error: 'חסרה קטגוריה L1' };

  const node1 = findNamedChild(roots, l1Name);
  if (!node1) {
    return { names: { category1: '', category2: '', category3: '' }, error: `קטגוריה L1 אינה בעץ: ${l1Name}` };
  }

  if (l3Name && !l2Name) {
    return { names: { category1: '', category2: '', category3: '' }, error: 'יש למלא קטגוריה L2 לפני L3' };
  }

  let node2: CategoryNode | undefined;
  if (l2Name) {
    node2 = findNamedChild(node1.children, l2Name);
    if (!node2) {
      return {
        names: { category1: '', category2: '', category3: '' },
        error: `קטגוריה L2 אינה תחת ${node1.name}: ${l2Name}`,
      };
    }
  }

  if (l3Name) {
    const node3 = findNamedChild(node2?.children, l3Name);
    if (!node3) {
      return {
        names: { category1: '', category2: '', category3: '' },
        error: `קטגוריה L3 אינה תחת ${node2?.name || node1.name}: ${l3Name}`,
      };
    }
    return {
      names: {
        category1: node1.name,
        category2: node2?.name || l2Name,
        category3: node3.name,
      },
    };
  }

  return {
    names: {
      category1: node1.name,
      category2: node2?.name || '',
      category3: '',
    },
  };
};

export const validateImportRow = (
  raw: ImportRawRow,
  tree: CategoryTree | null | undefined
): { valid?: ValidImportRow; error?: string } => {
  const vendorName = trimText(raw.vendorName);
  if (!vendorName) return { error: 'חסר שם ספק' };

  const amounts = resolveImportAmounts(raw.netAmount, raw.vatAmount, raw.totalAmount);
  if ('error' in amounts) return { error: amounts.error };

  const documentDate = parseImportDate(raw.documentDate);
  if (!documentDate) return { error: 'תאריך חשבונית חסר או לא בפורמט dd-mmm-yy' };

  const categories = resolveImportCategories(tree, raw.category1, raw.category2, raw.category3);
  if (categories.error) return { error: categories.error };

  const noVat = isNoVatCategory(
    tree,
    categories.names.category1,
    categories.names.category2,
    categories.names.category3
  );
  let netAmount = amounts.netAmount;
  let vatAmount = amounts.vatAmount;
  let totalAmount = amounts.totalAmount;
  if (noVat) {
    const noVatAmounts = amountsForNoVat(
      amounts.netAmount,
      amounts.vatAmount,
      amounts.totalAmount
    );
    netAmount = noVatAmounts.amount;
    vatAmount = noVatAmounts.vatAmount;
    totalAmount = noVatAmounts.totalAmount ?? noVatAmounts.amount;
  }

  return {
    valid: {
      row: raw.row,
      vendorName,
      netAmount,
      vatAmount,
      totalAmount,
      category1: categories.names.category1,
      category2: categories.names.category2,
      category3: categories.names.category3,
      projectTag: trimText(raw.projectTag),
      documentDate,
      invoiceNumber: trimText(raw.expenseId),
      notes: trimText(raw.notes),
      documentType: looksLikeCreditAmounts(netAmount, vatAmount, totalAmount) ? 'credit_note' : undefined,
    },
  };
};

export const isBlankImportRow = (raw: ImportRawRow): boolean =>
  !trimText(raw.vendorName) &&
  !trimText(raw.netAmount) &&
  !trimText(raw.vatAmount) &&
  !trimText(raw.totalAmount) &&
  !trimText(raw.category1) &&
  !trimText(raw.documentDate) &&
  !trimText(raw.category2) &&
  !trimText(raw.category3) &&
  !trimText(raw.projectTag) &&
  !trimText(raw.expenseId) &&
  !trimText(raw.notes);
